import * as Sentry from "@sentry/react";

interface SentryRuntimeConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  tracesSampleRate?: string;
}

const BUILD_UPLOAD_CAPABILITY = new RegExp(
  "(/(?:fin/)?api/builds/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/upload/)[0-9a-f]{64}",
  "gi",
);

const FIN_TRACE_TARGET = new RegExp(
  "^/fin/(?!api/builds/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/upload/)",
  "i",
);
const API_TRACE_TARGET = new RegExp(
  "^/api/(?!builds/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/upload/)",
  "i",
);

declare global {
  interface Window {
    __SENTRY_CONFIG__?: SentryRuntimeConfig;
  }
}

function optionalValue(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  return normalized || undefined;
}

export function parseTracesSampleRate(value: string | undefined): number {
  const normalized = value?.trim();
  if (!normalized) return 0;

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    throw new Error(
      "VITE_SENTRY_TRACES_SAMPLE_RATE must be a finite number between 0 and 1",
    );
  }
  return parsed;
}

/** Keep possession capabilities out of browser monitoring payloads. */
export function redactBuildUploadCapability(value: string): string {
  return value.replace(BUILD_UPLOAD_CAPABILITY, "$1[redacted]");
}

function redactStringProperties(record: Record<string, unknown> | undefined) {
  if (record === undefined) return;
  for (const [key, value] of Object.entries(record)) {
    if (typeof value === "string") {
      record[key] = redactBuildUploadCapability(value);
    }
  }
}

/** Initialize browser error monitoring from container-provided runtime config. */
export function initSentry(service: string): boolean {
  const config = window.__SENTRY_CONFIG__;
  const dsn = optionalValue(config?.dsn);
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: optionalValue(config?.environment),
    release: optionalValue(config?.release),
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: parseTracesSampleRate(config?.tracesSampleRate),
    // Capability uploads are deliberately possession-only: do not attach
    // distributed-tracing headers to those requests, and redact their token
    // from the local spans/breadcrumbs that still describe the request.
    tracePropagationTargets: [API_TRACE_TARGET, FIN_TRACE_TARGET],
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.message !== undefined) {
        breadcrumb.message = redactBuildUploadCapability(breadcrumb.message);
      }
      redactStringProperties(breadcrumb.data);
      return breadcrumb;
    },
    beforeSendSpan: (span) => {
      if (span.description !== undefined) {
        span.description = redactBuildUploadCapability(span.description);
      }
      redactStringProperties(span.data);
      return span;
    },
    beforeSend: (event) => {
      if (event.message !== undefined) {
        event.message = redactBuildUploadCapability(event.message);
      }
      if (event.transaction !== undefined) {
        event.transaction = redactBuildUploadCapability(event.transaction);
      }
      if (event.request?.url !== undefined) {
        event.request.url = redactBuildUploadCapability(event.request.url);
      }
      for (const breadcrumb of event.breadcrumbs ?? []) {
        if (breadcrumb.message !== undefined) {
          breadcrumb.message = redactBuildUploadCapability(breadcrumb.message);
        }
        redactStringProperties(breadcrumb.data);
      }
      for (const exception of event.exception?.values ?? []) {
        if (exception.value !== undefined) {
          exception.value = redactBuildUploadCapability(exception.value);
        }
      }
      return event;
    },
    // Transaction events bypass beforeSend. Redact their request and scoped
    // breadcrumbs independently so a sampled navigation can never carry a
    // possession token even though the child span is already sanitized.
    beforeSendTransaction: (event) => {
      if (event.transaction !== undefined) {
        event.transaction = redactBuildUploadCapability(event.transaction);
      }
      if (event.request?.url !== undefined) {
        event.request.url = redactBuildUploadCapability(event.request.url);
      }
      for (const breadcrumb of event.breadcrumbs ?? []) {
        if (breadcrumb.message !== undefined) {
          breadcrumb.message = redactBuildUploadCapability(breadcrumb.message);
        }
        redactStringProperties(breadcrumb.data);
      }
      return event;
    },
    sendDefaultPii: false,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    initialScope: { tags: { service } },
  });
  return true;
}
